// query/serviceFactory.ts
import { queryClient } from "../config/queryClient";
import { useQuery, useMutation } from "@tanstack/react-query";
import { baseService } from "./baseService";

export function createService(resource) {
  const baseUrl = `/${resource}`;
  const key = (suffix, params) => [resource, suffix, params];

  // shared optimistic update factory for both PUT/PATCH
  function useUpdateMutation(method) {
    return useMutation({
      mutationFn: ({ id, body }) =>
        method === "put"
          ? baseService.put(`${baseUrl}/${id}`, body)
          : baseService.patch(`${baseUrl}/${id}`, body),

      onMutate: async ({ id, body }) => {
        // cancel detail query
        await queryClient.cancelQueries({
          queryKey: [resource, "detail", id],
        });

        const previous = queryClient.getQueryData([resource, "detail", id]);

        // optimistic merge
        queryClient.setQueryData([resource, "detail", id], (old) => ({
          ...old,
          ...body,
        }));

        return { previous };
      },

      onError: (_err, { id }, ctx) => {
        if (ctx?.previous) {
          queryClient.setQueryData([resource, "detail", id], ctx.previous);
        }
      },

      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [resource, "list"] });
      },
    });
  }

  return {
    // LIST
    useList: (params = {}, opt = {}) =>
      useQuery({
        queryKey: key("list", params),
        queryFn: () => baseService.get(baseUrl, params),
        keepPreviousData: true,
        ...opt,
      }),

    // GET DETAIL
    useGet: (id) =>
      useQuery({
        queryKey: key("detail", id),
        queryFn: () => baseService.get(`${baseUrl}/${id}`),
        enabled: !!id,
      }),

    // CREATE
    useCreate: () =>
      useMutation({
        mutationFn: (body) => baseService.post(baseUrl, body),
        onSuccess: () =>
          queryClient.invalidateQueries({ queryKey: [resource, "list"] }),
      }),

    // UPDATE (PUT)
    useUpdatePut: () => useUpdateMutation("put"),

    // UPDATE (PATCH)
    useUpdatePatch: () => useUpdateMutation("patch"),

    // DELETE
    useDelete: () =>
      useMutation({
        mutationFn: (id) => baseService.delete(`${baseUrl}/${id}`),

        onMutate: async (id) => {
          await queryClient.cancelQueries({ queryKey: [resource, "list"] });

          const previous = queryClient.getQueryData([resource, "list"]);

          queryClient.setQueryData([resource, "list"], (old) => ({
            ...old,
            data: old?.data?.filter((i) => i.id !== id),
          }));

          return { previous };
        },

        onError: (_err, _id, ctx) => {
          if (ctx?.previous) {
            queryClient.setQueryData([resource, "list"], ctx.previous);
          }
        },

        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: [resource, "list"] });
        },
      }),

    // INFINITE
    getInfiniteKey: (params) => [resource, "infinite", params],

    useInfinite: (params = {}) =>
      useQuery({
        queryKey: key("infinite", params),
        queryFn: () => baseService.get(baseUrl, params),
        keepPreviousData: true,
      }),
  };
}
